import React, { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from '@jjj/components';
import { RootState } from '../../redux/store';
import { getStore } from '../../redux/storeAccessor';
import notificationActions from '../../redux/notifications/actions';
import { isPermissionGranted, PermissionCheck } from '../../redux/permissions/helpers';
import { t } from '../../i18n';

interface OwnProps {
  permission: PermissionCheck;
  redirectTo: string;
  toastKey?: string;
  children: React.ReactNode;
}

interface NotificationPayload {
  type: string;
  headline: string;
  message: string;
}

interface InjectedProps {
  isLoaded: boolean;
  granted: boolean;
  addNotification: (payload: NotificationPayload) => void;
}

type PermissionRouteProps = Omit<OwnProps, 'permission'> & InjectedProps;

export const PermissionRoute = ({
  redirectTo,
  toastKey,
  children,
  isLoaded,
  granted,
  addNotification,
}: PermissionRouteProps) => {
  const location = useLocation();
  const blocked = isLoaded && !granted;

  useEffect(() => {
    if (blocked) {
      addNotification({
        type: 'warning',
        headline: t('dash.common.notification.forbidden_headline'),
        message: toastKey ? t(toastKey) : t('dash.common.notification.forbidden_error'),
      });
    }
  }, [blocked, addNotification, toastKey]);

  if (!isLoaded) return <Loader />;

  if (blocked) {
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const emptySubscribe = () => () => undefined;

const PermissionRouteContainer = ({ permission, redirectTo, toastKey, children }: OwnProps) => {
  const store = getStore();

  const subscribe = useMemo(
    () => (store ? (listener: () => void) => store.subscribe(listener) : emptySubscribe),
    [store],
  );

  const getPermissionsData = useCallback(
    () => (store?.getState() as RootState | undefined)?.Permissions.data ?? null,
    [store],
  );

  const permissionsData = useSyncExternalStore(subscribe, getPermissionsData, getPermissionsData);
  const isLoaded = permissionsData !== null;
  const granted = isPermissionGranted(permissionsData, permission);

  const addNotification = useCallback(
    (payload: NotificationPayload) => {
      store?.dispatch(notificationActions.addNotification(payload));
    },
    [store],
  );

  return (
    <PermissionRoute
      redirectTo={redirectTo}
      toastKey={toastKey}
      isLoaded={isLoaded}
      granted={granted}
      addNotification={addNotification}
    >
      {children}
    </PermissionRoute>
  );
};

export default PermissionRouteContainer;
